import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ACCENTS, useAppearance } from './theme.js';
import type { AccentDef, Appearance, ThemeMode } from './theme.js';

/**
 * Appearance control (shell head): a segmented Light / Dark /
 * System switch with sun, moon, and monitor icons, and the theme
 * color picker behind the current-color button. Owner direction,
 * 2026-10-18, modeled on the settings panel he supplied; the
 * picker itself follows the maineblock.com site-menu pattern he
 * pointed to: clicking the color button expands a strip in the
 * header actions directly beside the mode switch, and selecting
 * a color leaves it open so several can be tried. Placement
 * amended by owner direction, 2026-10-24 (all three Studio
 * shells): the strip sits inline in the header actions — no
 * full-width band under the header bar — and its label reads
 * "Theme Color". The control is presentation only; mode and
 * accent persist through ui/theme.ts. State is shared through a
 * context because the button and the strip both live in the
 * header actions.
 */

function SunIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function MonitorIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

const MODES: readonly {
  readonly id: ThemeMode;
  readonly label: string;
  readonly Icon: () => React.JSX.Element;
}[] = [
  { id: 'light', label: 'Light mode', Icon: SunIcon },
  { id: 'dark', label: 'Dark mode', Icon: MoonIcon },
  { id: 'system', label: 'System mode', Icon: MonitorIcon },
];

interface AppearanceUi extends Appearance {
  readonly stripOpen: boolean;
  readonly setStripOpen: (open: boolean) => void;
}

const AppearanceContext = createContext<AppearanceUi | null>(null);

function useAppearanceUi(): AppearanceUi {
  const ctx = useContext(AppearanceContext);
  if (ctx === null) {
    throw new Error(
      'Appearance controls must be mounted inside <AppearanceProvider>.',
    );
  }
  return ctx;
}

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const appearance = useAppearance();
  const [stripOpen, setStripOpen] = useState(false);

  /* The strip closes on Escape while it is open; picking a color
     does not close it (see the component header). */
  useEffect(() => {
    if (!stripOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setStripOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [stripOpen]);

  return (
    <AppearanceContext.Provider
      value={{ ...appearance, stripOpen, setStripOpen }}
    >
      {children}
    </AppearanceContext.Provider>
  );
}

/**
 * The active accent definition, for components outside the shell
 * head that name the accent in copy (Step 3's chart caption names
 * its color). Requires the AppearanceProvider, which the shell
 * mounts around the whole app.
 */
export function useActiveAccent(): AccentDef {
  const { accent } = useAppearanceUi();
  return ACCENTS.find((a) => a.id === accent) ?? ACCENTS[0];
}

export function AppearanceControl() {
  const { mode, accent, setMode, stripOpen, setStripOpen } = useAppearanceUi();

  const activeAccent = ACCENTS.find((a) => a.id === accent) ?? ACCENTS[0];

  return (
    <div className="appearance">
      <div className="seg" role="group" aria-label="Color mode">
        {MODES.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            className={mode === id ? 'on' : undefined}
            aria-pressed={mode === id}
            aria-label={label}
            title={label}
            onClick={() => setMode(id)}
          >
            <Icon />
          </button>
        ))}
      </div>
      <button
        type="button"
        className="swatch-btn"
        style={{ background: activeAccent.color }}
        aria-expanded={stripOpen}
        aria-controls="accent-strip"
        aria-label={`Theme color: ${activeAccent.label}`}
        title="Theme color"
        onClick={() => setStripOpen(!stripOpen)}
      />
    </div>
  );
}

/**
 * The expanded theme color picker: an inline strip in the
 * header actions beside the mode switch (rendered only while
 * open). Selecting a swatch applies the color and leaves the
 * strip open; the color button or Escape closes it.
 */
export function AccentStrip() {
  const { accent, setAccent, stripOpen } = useAppearanceUi();
  if (!stripOpen) return null;
  return (
    <div className="accent-strip" id="accent-strip">
      <div className="accent-strip-label">Theme Color</div>
      <div className="accent-row">
        {ACCENTS.map((a) => (
          <button
            key={a.id}
            type="button"
            className={`swatch${accent === a.id ? ' on' : ''}`}
            style={{ background: a.color }}
            aria-pressed={accent === a.id}
            aria-label={a.label}
            title={a.label}
            onClick={() => setAccent(a.id)}
          />
        ))}
      </div>
    </div>
  );
}
